import { NavLink, Link, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, BarChart3, Package,
    Tags, Ruler, Settings as SettingsIcon,
    UsersRound, LayoutPanelLeft, Table2, Shirt, ImageIcon,
    Plug, ChevronUp, LogOut, User,
    Sparkles,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/brand/Logo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const groups = [
    {
        sectionKey: "nav.section.workspace",
        items: [
            { to: "/app", end: true, labelKey: "nav.dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
            { to: "/app/analytics", labelKey: "nav.analytics", icon: BarChart3, testId: "nav-analytics" },
            { to: "/app/widgets/panel", labelKey: "nav.sizing-panel", icon: LayoutPanelLeft, testId: "nav-sizing-panel" },
            { to: "/app/experiences/size-chart", labelKey: "nav.size-chart-modal", icon: Table2, testId: "nav-size-chart-modal" },
            { to: "/app/experiences/find-my-size", labelKey: "nav.find-my-size-wizard", icon: Sparkles, testId: "nav-find-my-size-wizard" },
        ],
    },
    {
        sectionKey: "nav.section.catalog",
        items: [
            { to: "/app/catalog/products", labelKey: "nav.products", icon: Package, testId: "nav-products" },
            { to: "/app/catalog/tables", labelKey: "nav.tables", icon: Table2, testId: "nav-tables" },
        ],
    },
    {
        sectionKey: "nav.section.sizing",
        items: [
            { to: "/app/sizing/sizes", labelKey: "nav.size-series", icon: Tags, testId: "nav-size-series" },
            { to: "/app/sizing/measurement-groups", labelKey: "nav.measurement-groups", icon: Ruler, testId: "nav-measurement-groups" },
            { to: "/app/sizing/fit-info", labelKey: "nav.fit-info", icon: Shirt, testId: "nav-fit-info" },
            { to: "/app/sizing/models", labelKey: "nav.models", icon: UsersRound, testId: "nav-models" },
            { to: "/app/sizing/images", labelKey: "nav.images", icon: ImageIcon, testId: "nav-images" },
        ],
    },
    {
        sectionKey: "nav.section.system",
        items: [
            { to: "/app/integration", labelKey: "nav.integration", icon: Plug, testId: "nav-integration" },
            { to: "/app/settings", labelKey: "nav.settings", icon: SettingsIcon, testId: "nav-settings" },
        ],
    },
];

const initials = (name = "") =>
    name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";

export default function Sidebar() {
    const { t } = useI18n();
    const { session, logout } = useAuth();
    const nav = useNavigate();
    const user = session?.user;
    const tenant = session?.tenant;

    const handleLogout = async () => {
        await logout();
        nav("/", { replace: true });
    };

    return (
        <aside
            className="hidden lg:flex w-[252px] shrink-0 flex-col border-r border-border bg-background sticky top-0 h-screen"
            data-testid="app-sidebar"
        >
            <div className="h-16 flex items-center justify-between pl-5 pr-5 border-b border-border">
                <Logo />
            </div>

            <nav className="flex-1 overflow-y-auto scroll-thin px-3 py-5 space-y-6">
                {groups.map((g) => (
                    <Section key={g.sectionKey} label={t(g.sectionKey)}>
                        {g.items.map((it) => (
                            <NavItem key={it.to} {...it} t={t} />
                        ))}
                    </Section>
                ))}
            </nav>

            {/* Kullanıcı ayak bloğu — Topbar'dan taşındı (2026-02-27 kullanıcı isteği).
                Avatar + isim + tenant + dropdown menu (Ayarlar + Çıkış). */}
            <div className="shrink-0 border-t border-border px-3 py-3" data-testid="sidebar-user-footer">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted/60 transition-colors group"
                            data-testid="sidebar-user-menu"
                        >
                            <Avatar className="h-8 w-8 border border-border shrink-0">
                                <AvatarFallback className="bg-foreground text-background text-[11px] font-semibold">
                                    {initials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0 text-left leading-tight">
                                <div className="text-[12.5px] font-medium truncate" data-testid="sidebar-user-name">
                                    {user?.name || "—"}
                                </div>
                                <div className="text-[10.5px] text-muted-foreground truncate" data-testid="sidebar-user-tenant">
                                    {tenant?.site_domain || tenant?.company_name || ""}
                                </div>
                            </div>
                            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={2} />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align="start" className="w-[228px]">
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col leading-tight">
                                <span className="text-sm font-medium">{user?.name || "—"}</span>
                                <span className="text-xs text-muted-foreground">{user?.email}</span>
                                <span className="text-[10px] text-muted-foreground mt-1">
                                    {user?.role === "owner" ? "Hesap Sahibi" : "Ekip Üyesi"} · {tenant?.company_name}
                                </span>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link to="/app/settings" className="cursor-pointer">
                                <User className="h-3.5 w-3.5 mr-2" /> {t("settings.title")}
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" data-testid="sidebar-logout-btn">
                            <LogOut className="h-3.5 w-3.5 mr-2" /> Çıkış yap
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </aside>
    );
}

const Section = ({ label, children }) => (
    <div>
        <div className="px-2 mb-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
            {label}
        </div>
        <div className="space-y-0.5">{children}</div>
    </div>
);

const NavItem = ({ to, end, icon: Icon, labelKey, label, t, testId }) => (
    <NavLink
        to={to}
        end={end}
        data-testid={testId}
        className={({ isActive }) =>
            [
                "group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-[13px] transition-colors",
                isActive
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            ].join(" ")
        }
    >
        {({ isActive }) => (
            <>
                <span
                    aria-hidden
                    className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm ${isActive ? "bg-foreground" : "bg-transparent"}`}
                />
                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span>{label || t(labelKey)}</span>
            </>
        )}
    </NavLink>
);
